<template>
  <div class="controls">
    <div class="control-group">
      <div class="action-buttons">
        <el-button @click="showSettingsDialog">其它</el-button>
        <el-button @click="showUnfinishedStudents">欠交名单</el-button>
        <el-button @click="showImportStudentsDialog">批量录入</el-button>
        <el-button @click="openManualQuickRecord">手动快录</el-button>
      </div>
      
      <div class="date-type-row">
        <div class="date-controls">
          <div class="date-picker-container">
            <el-date-picker
              ref="datePicker"
              v-model="localCurrentDate"
              type="date"
              placeholder="选择日期"
              :format="dateFormat"
              value-format="yyyy-MM-dd"
              @change="handleDateChange"
              @blur="onDatePickerBlur"
              @focus="onDatePickerFocus"
              :clearable="false"
              style="width: 100%; cursor: pointer;"
              :editable="false"
              :picker-options="pickerOptions">
            </el-date-picker>
          </div>
          <div class="nav-buttons">
            <el-button @click="prevDay">前一天</el-button>
            <el-button @click="nextDay">后一天</el-button>
          </div>
          <div class="nav-buttons">
            <el-button @click="prevAssignment">前一次</el-button>
            <el-button @click="nextAssignment">后一次</el-button>
          </div>
        </div>
        
        <div class="type-selector-container">
          <el-select v-model="localSelectedType" @change="handleTypeChange" placeholder="请选择作业类型" style="width: 100%;">
            <el-option
              v-for="type in homeworkTypes"
              :key="type.id"
              :label="type.name"
              :value="type.id">
            </el-option>
          </el-select>
          <div class="stat-buttons-row">
            <template v-if="!isAssigned">
              <div class="stat-button-item assign-homework">
                <el-button
                  @click="handleAssignHomework"
                  :class="[{ 'flash-animation': shouldFlash }, subjectClass]"
                  class="assign-homework-btn">
                  布置作业
                </el-button>
              </div>
            </template>
            <template v-else>
              <div class="stat-button-item">
                <div class="stat-number finished">{{ finishedCount }}</div>
                <div class="stat-label">已订正</div>
              </div>
              <div class="stat-button-item">
                <div class="stat-number unfinished">{{ unfinishedCount }}</div>
                <div class="stat-label">未订正</div>
              </div>
            </template>
            <div class="stat-button-item">
              <el-button 
                @click="toggleUnfinishedCount" 
                :class="{'unfinished-count-btn': true, 'active': showUnfinishedCount}"
                size="mini">
                <div class="btn-text-two-lines">欠交<br/>数量</div>
              </el-button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="content-input-container">
        <el-input
          v-model="localHomeworkContent"
          placeholder="请输入作业内容"
          style="width: 100%;"
          @focus="showHomeworkContentDialog"
          readonly>
        </el-input>
        <el-button 
          @click="switchSubject" 
          class="subject-toggle-btn-inline"
          :class="subjectClass"
          style="color: white;">
          {{ currentSubject }}
        </el-button>
      </div>
      
      <!-- <div class="search-container">
        <span>彻查学生：</span>
        <el-autocomplete
          v-model="searchKeyword"
          :fetch-suggestions="querySearch"
          placeholder="请输入学号或姓名"
          @select="handleSelectStudent"
          style="flex: 1;"
          :trigger-on-focus="true">
          <template slot-scope="{ item }">
            <div class="name">{{ item.studentNumber }} - {{ item.name }}</div>
          </template>
        </el-autocomplete>
      </div> -->
    </div>
    
    <!-- 设置弹窗 -->
    <MobileSettingsDialog
      :visible.sync="settingsDialogVisible"
      :isAssigned="isAssigned"
      @reset-status="handleResetStatus"
      @set-all-finished="handleSetAllFinished" />
    
    <!-- 作业内容输入对话框 -->
    <MobileHomeworkContentDialog
      :visible.sync="homeworkContentDialogVisible"
      :content="tempHomeworkContent"
      @close="closeHomeworkContentDialog"
      @save="saveHomeworkContentFromDialog"
      @opened="focusHomeworkContent" />
    
    <!-- 批量录入对话框 -->
    <MobileImportStudentsDialog
      :visible.sync="importStudentsDialogVisible"
      :text="importStudentsText"
      :students="students"
      :current-date="localCurrentDate"
      :selected-type="localSelectedType"
      :correction-records="correctionRecords"
      :is-assigned="isAssigned"
      :class-id="classId"
      @update:text="val => importStudentsText = val"
      @loadData="$emit('loadData')"
      @show-unmatched="handleShowUnmatched" />
    
    <!-- 无法匹配的学生名单对话框 -->
    <MobileUnmatchedStudentsDialog
      :visible.sync="unmatchedStudentsDialogVisible"
      :text="unmatchedStudentsText" />
    
    <!-- 手动快录对话框 -->
    <MobileManualQuickRecordDialog
      :visible.sync="manualQuickRecordVisible"
      :selected="manualQuickRecordSelected"
      :data="manualQuickRecordData"
      :is-assigned="isAssigned"
      :subject-class="subjectClass"
      @close="handleManualQuickRecordClose"
      @before-close="handleManualQuickRecordBeforeClose"
      @cancel="handleManualQuickRecordCancel"
      @confirm="handleManualQuickRecordConfirm"
      @move-to-right="handleMoveToRight"
      @move-to-left="handleMoveToLeft"
      @filter-method="handleManualQuickRecordFilterMethod"
      @selected-changed="handleSelectedChanged"
      ref="manualQuickRecordDialog" />
    
    <!-- 学生历史欠交记录对话框 -->
    <MobileStudentHistoryDialog
      :visible.sync="studentHistoryDialogVisible"
      :selected-student-name="selectedStudentName"
      :subject-name="currentSubject"
      :records="studentHistoryRecords"
      :show-no-records="showStudentNoRecords"
      :sort-field="studentHistorySortField"
      :sort-order="studentHistorySortOrder"
      @sort="handleStudentHistorySort"
      @mark="handleStudentHistoryMarkAsFinished"
      @edit="handleEditHomeworkContent"
      @locate="handleLocateToRecord"
      @export="handleExportStudentData" />
    
    <!-- 历史欠交学生对话框 -->
    <MobileUnfinishedStudentsDialog
      :visible.sync="unfinishedStudentsDialogVisible"
      :students="unfinishedStudents"
      :type-name="currentTypeName"
      :sort-field="unfinishedStudentsSortField"
      :sort-order="unfinishedStudentsSortOrder"
      @sort="handleUnfinishedStudentsSort"
      @mark="handleUnfinishedStudentsMarkAsFinished"
      @edit="handleEditHomeworkContent"
      @locate="handleLocateToRecord" />
  </div>
</template>

<script>
import Utils from '../../utils/Utils.js'
import EventBus from '../../utils/EventBus'

// 移动端弹窗组件
import MobileStudentHistoryDialog from '@/components/mobile/mobile_dialogs/StudentHistoryDialogMobile.vue'
import MobileUnfinishedStudentsDialog from '@/components/mobile/mobile_dialogs/UnfinishedStudentsDialogMobile.vue'
import MobileHomeworkContentDialog from '@/components/mobile/mobile_dialogs/HomeworkContentDialogMobile.vue'
import MobileImportStudentsDialog from '@/components/mobile/mobile_dialogs/ImportStudentsDialogMobile.vue'
import MobileUnmatchedStudentsDialog from '@/components/mobile/mobile_dialogs/UnmatchedStudentsDialogMobile.vue'
import MobileManualQuickRecordDialog from '@/components/mobile/mobile_dialogs/ManualQuickRecordDialogMobile.vue'
import MobileSettingsDialog from '@/components/mobile/mobile_dialogs/SettingsDialogMobile.vue'

export default {
  name: 'ControlPanelMobile',
  components: {
    MobileStudentHistoryDialog,
    MobileUnfinishedStudentsDialog,
    MobileHomeworkContentDialog,
    MobileImportStudentsDialog,
    MobileUnmatchedStudentsDialog,
    MobileManualQuickRecordDialog,
    MobileSettingsDialog
  },
  props: {
    classId: {
      type: Number,
      default: null
    },
    subjectIndex: {
      type: Number,
      default: 0
    },
    students: {
      type: Array,
      default: () => []
    },
    classManagementForm: {
      type: Object,
      default: () => ({})
    },
    // 使用.sync修饰符实现双向绑定
    selectedType: {
      type: Number,
      default: 1
    },
    currentDate: {
      type: String,
      default: ''
    },
    homeworkContent: {
      type: String,
      default: ''
    },
    homeworkTypes: {
      type: Array,
      default: () => []
    },
    correctionRecords: {
      type: Array,
      default: () => []
    },
    showUnfinishedCount: {
      type: Boolean,
      default: false
    },
    finishedCount: {
      type: Number,
      default: 0
    },
    unfinishedCount: {
      type: Number,
      default: 0
    },
    isAssigned: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 本地状态，用于组件内部操作
      localSelectedType: this.selectedType,
      localCurrentDate: this.currentDate || this.getCurrentDate(),
      localHomeworkContent: this.homeworkContent,
      searchKeyword: '',
      tempHomeworkContent: '',
      editingRow: null,
      // 日期选择器选项
      pickerOptions: Utils.createDatepickerOptions(this),
      // 学生历史记录排序状态
      studentHistorySortField: 'typeName',
      studentHistorySortOrder: 'asc',
      // 历史欠交学生排序状态
      unfinishedStudentsSortField: 'date',
      unfinishedStudentsSortOrder: 'asc',
      // 设置弹窗
      settingsDialogVisible: false,
      // 作业内容弹窗
      homeworkContentDialogVisible: false,
      // 批量录入相关
      importStudentsDialogVisible: false,
      importStudentsText: '',
      unmatchedStudentsDialogVisible: false,
      unmatchedStudentsText: '',
      // 手动快录相关
      manualQuickRecordVisible: false,
      manualQuickRecordData: [],
      manualQuickRecordSelected: [],
      // 学生历史欠交记录对话框
      studentHistoryDialogVisible: false,
      studentHistoryRecords: [],
      selectedStudentName: '',
      showStudentNoRecords: false,
      // 历史欠交学生对话框
      unfinishedStudentsDialogVisible: false,
      unfinishedStudents: [],
      // 布置作业按钮闪烁状态
      shouldFlash: false
    }
  },
  computed: {
    currentSubject() {
      const subjects = ['语文', '数学', '英语'];
      return subjects[this.subjectIndex];
    },
    subjectClass() {
      switch(this.subjectIndex) {
        case 0:
          return 'chinese-subject';
        case 1:
          return 'math-subject';
        case 2:
          return 'english-subject';
        default:
          return 'math-subject';
      }
    },
    currentTypeName() {
      const currentType = this.homeworkTypes.find(type => type.id === this.localSelectedType);
      return currentType ? currentType.name : '';
    },
    dateLabel() {
      if (!this.localCurrentDate) return '';
      const selected = new Date(this.localCurrentDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selected.setHours(0, 0, 0, 0);
      const diffDays = Math.round((selected.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return '今天';
      if (diffDays === -1) return '昨天';
      if (diffDays === -2) return '前天';
      const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
      };
      const todayWeekStart = getWeekStart(today);
      const selectedWeekStart = getWeekStart(selected);
      const weekDiff = Math.round((selectedWeekStart.getTime() - todayWeekStart.getTime()) / (7 * 24 * 60 * 60 * 1000));
      const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
      const dayOfWeek = weekDays[selected.getDay()];
      if (weekDiff === 0) {
        return '本周' + dayOfWeek;
      } else if (weekDiff === -1) {
        return '上周' + dayOfWeek;
      }
      return '';
    },
    dateFormat() {
      const label = this.dateLabel;
      return label ? `MM月dd日 ${label}` : 'MM月dd日';
    }
  },
  watch: {
    // 监听props变化，更新本地状态
    selectedType: {
      handler(newVal) {
        this.localSelectedType = newVal
      },
      immediate: true
    },
    currentDate: {
      handler(newVal) {
        this.localCurrentDate = newVal
      },
      immediate: true
    },
    studentHistoryDialogVisible(newVal) {
      if (!newVal) {
        // 弹窗关闭时重置状态
        this.showStudentNoRecords = false;
        this.studentHistoryRecords = [];
        this.selectedStudentName = '';
      }
    },
    homeworkContent: {
      handler(newVal) {
        this.localHomeworkContent = newVal
      },
      immediate: true
    },
    homeworkTypes: {
      handler(newVal) {
        // 当作业类型列表变化时，确保选中的类型仍然有效
        if (newVal.length > 0 && !newVal.some(type => type.id === this.localSelectedType)) {
          this.localSelectedType = newVal[0].id
          this.$emit('update:selectedType', this.localSelectedType)
        }
      },
      immediate: true
    }
  },
  methods: {
    // 获取当前日期
    getCurrentDate() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 切换欠交数量显示
    toggleUnfinishedCount() {
      this.$emit('toggle-unfinished-count')
    },
    
    // 切换科目
    switchSubject() {
      this.$emit('switch-subject')
    },
    
    // 处理作业类型变化
    handleTypeChange() {
      this.$emit('update:selectedType', this.localSelectedType)
      this.$emit('loadData')
    },
    
    // 处理日期变化
    handleDateChange() {
      this.$emit('update:currentDate', this.localCurrentDate)
      this.$emit('loadData')
    },
    
    // 前一天
    prevDay() {
      const date = new Date(this.localCurrentDate)
      date.setDate(date.getDate() - 1)
      this.localCurrentDate = this.formatDate(date)
      this.$emit('update:currentDate', this.localCurrentDate)
      this.$emit('loadData')
    },
    
    // 后一天
    nextDay() {
      const date = new Date(this.localCurrentDate)
      date.setDate(date.getDate() + 1)
      this.localCurrentDate = this.formatDate(date)
      this.$emit('update:currentDate', this.localCurrentDate)
      this.$emit('loadData')
    },
    
    // 前一次作业
    prevAssignment() {
      // 获取前一次布置作业的日期
      let url = `/correctionRecords/previousAssignedDate?typeId=${this.localSelectedType}&currentDate=${this.localCurrentDate}`
      if (this.classId) {
        url += `&classId=${this.classId}`
      }
      this.$http.get(url)
        .then(response => {
          if (response.data) {
            this.localCurrentDate = response.data
            this.$emit('update:currentDate', this.localCurrentDate)
            this.$emit('loadData')
          } else {
            this.$message({
              type: 'info',
              message: '当前已是该类型作业最早的布置日期'
            })
          }
        })
        .catch(error => {
          console.error('获取前一次布置日期失败:', error)
          this.$message({
            type: 'error',
            message: '获取前一次布置日期失败'
          })
        })
    },
    
    // 后一次作业
    nextAssignment() {
      // 获取后一次布置作业的日期
      let url = `/correctionRecords/nextAssignedDate?typeId=${this.localSelectedType}&currentDate=${this.localCurrentDate}`
      if (this.classId) {
        url += `&classId=${this.classId}`
      }
      this.$http.get(url)
        .then(response => {
          if (response.data) {
            this.localCurrentDate = response.data
            this.$emit('update:currentDate', this.localCurrentDate)
            this.$emit('loadData')
          } else {
            this.$message({
              type: 'info',
              message: '当前已是该类型作业最后的布置日期'
            })
          }
        })
        .catch(error => {
          console.error('获取后一次布置日期失败:', error)
          this.$message({
            type: 'error',
            message: '获取后一次布置日期失败'
          })
        })
    },
    
    // 显示作业内容对话框
    showHomeworkContentDialog() {
      this.editingRow = {
        id: null,
        date: this.localCurrentDate,
        homeworkTypeId: this.localSelectedType
      };
      this.tempHomeworkContent = this.localHomeworkContent; // 使用当前输入框的内容
      this.$nextTick(() => {
        this.homeworkContentDialogVisible = true;
      });
    },
    
    // 关闭作业内容对话框
    closeHomeworkContentDialog() {
      this.homeworkContentDialogVisible = false;
      this.tempHomeworkContent = '';
    },
    
    // 保存作业内容
    saveHomeworkContentFromDialog(content) {
      if (!this.editingRow) {
        console.error('editingRow is null or undefined');
        return;
      }
      
      const editingRowDate = this.editingRow.date;
      const editingRowHomeworkTypeId = this.editingRow.homeworkTypeId;
      
      let formattedDate = editingRowDate;
      if (typeof editingRowDate === 'string' && editingRowDate.includes('T')) {
        const d = new Date(editingRowDate);
        formattedDate = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
      }
      
      const homeworkContentData = {
        date: formattedDate,
        homeworkTypeId: editingRowHomeworkTypeId,
        content: content
      };
      
      if (this.classId) {
        homeworkContentData.classId = this.classId;
      }
      
      this.$http.post('/homeworkContents', homeworkContentData)
        .then(() => {
          this.$message({
            type: 'success',
            message: '作业内容保存成功!'
          });
          
          this.localHomeworkContent = content;
          this.$emit('update:homeworkContent', content);
          
          if (formattedDate === this.localCurrentDate && editingRowHomeworkTypeId === this.localSelectedType) {
            this.$emit('loadData');
          }
          
          if (this.studentHistoryRecords && this.studentHistoryRecords.includes(this.editingRow)) {
            this.editingRow.content = content;
            for (let i = 0; i < this.studentHistoryRecords.length; i++) {
              const record = this.studentHistoryRecords[i];
              const recordDate = typeof record.date === 'string' && record.date.includes('T')
                ? `${new Date(record.date).getFullYear()}-${(new Date(record.date).getMonth() + 1).toString().padStart(2, '0')}-${new Date(record.date).getDate().toString().padStart(2, '0')}`
                : record.date;
              if (recordDate === formattedDate && record.homeworkTypeId === editingRowHomeworkTypeId) {
                this.$set(this.studentHistoryRecords, i, {
                  ...record,
                  content: content
                });
              }
            }
          }
          
          if (this.unfinishedStudents && this.unfinishedStudents.includes(this.editingRow)) {
            this.editingRow.content = content;
            for (let i = 0; i < this.unfinishedStudents.length; i++) {
              const student = this.unfinishedStudents[i];
              const studentDate = student.rawDate || student.date;
              if (studentDate === formattedDate) {
                this.$set(this.unfinishedStudents, i, {
                  ...student,
                  content: content
                });
              }
            }
          }
        })
        .catch(error => {
          this.$message({
            type: 'error',
            message: '作业内容保存失败: ' + (error.response?.data?.message || error.message || '未知错误')
          });
        })
        .finally(() => {
          this.homeworkContentDialogVisible = false;
          this.editingRow = null;
        });
    },
    
    // 聚焦作业内容输入框
    focusHomeworkContent() {
      this.$nextTick(() => {
        if (this.$refs.homeworkContentInput) {
          this.$refs.homeworkContentInput.focus();
        }
      });
    },
    
    // 显示设置对话框
    showSettingsDialog() {
      this.settingsDialogVisible = true;
    },
    
    // 重置状态
    handleResetStatus() {
      this.$emit('reset-status');
    },
    
    // 设置全部完成
    handleSetAllFinished() {
      this.$emit('set-all-finished');
    },
    
    // 显示批量录入对话框
    showImportStudentsDialog() {
      this.importStudentsDialogVisible = true;
    },
    
    // 处理显示未匹配的学生名单
    handleShowUnmatched(text) {
      this.unmatchedStudentsText = text;
      this.unmatchedStudentsDialogVisible = true;
    },
    
    // 打开手动快录对话框
    async openManualQuickRecord() {
      // 转换学生数据为穿梭框需要的格式
      this.manualQuickRecordData = this.students.map(student => {
        // 对1到9号学生添加前导0
        let displayNumber = student.studentNumber;
        const studentNum = parseInt(student.studentNumber);
        if (studentNum >= 1 && studentNum <= 9) {
          displayNumber = '0' + studentNum;
        }
        
        return {
          key: student.id,
          label: `${displayNumber}.${student.name}`,
          studentNumber: student.studentNumber,
          name: student.name,
          pinyinInitials: student.pinyinInitials || ''
        };
      })
      // 按学号数字大小排序
      .sort((a, b) => {
        const numA = parseInt(a.studentNumber);
        const numB = parseInt(b.studentNumber);
        return numA - numB;
      });
      
      this.manualQuickRecordSelected = [];
      
      // 检查是否有暂存数据
      try {
        const response = await this.$http.get('/manualQuickRecordTemp', {
          params: {
            classId: this.classId,
            date: this.currentDate,
            typeId: this.localSelectedType
          }
        });
        
        console.log('查询暂存数据响应:', response);
        console.log('response.data:', response.data);
        console.log('response.data.studentIds:', response.data?.studentIds);
        
        if (response.data && response.data.studentIds && response.data.studentIds.length > 0) {
          // 有暂存数据，询问用户是否使用
          this.$confirm('检测到云端暂存的手动快录数据，是否使用？', '提示', {
            confirmButtonText: '使用',
            cancelButtonText: '不使用',
            type: 'info',
            customClass: this.subjectClass + '-confirm'
          }).then(() => {
            // 用户选择使用暂存数据
            this.manualQuickRecordSelected = [...response.data.studentIds];
            this.manualQuickRecordVisible = true;
          }).catch(() => {
            // 用户选择不使用暂存数据
            this.manualQuickRecordVisible = true;
          });
        } else {
          // 没有暂存数据，直接打开弹窗
          console.log('没有暂存数据或studentIds为空，直接打开弹窗');
          this.manualQuickRecordVisible = true;
        }
      } catch (error) {
        console.error('查询暂存数据失败:', error);
        // 查询失败，直接打开弹窗
        this.manualQuickRecordVisible = true;
      }
    },
    
    // 手动快录相关方法
    handleManualQuickRecordClose() {
      this.manualQuickRecordVisible = false;
    },
    
    handleManualQuickRecordBeforeClose(done) {
      done();
    },
    
    handleManualQuickRecordCancel() {
      this.manualQuickRecordVisible = false;
    },
    
    handleManualQuickRecordConfirm(selectedIds) {
      const idsToUse = selectedIds || this.manualQuickRecordSelected;
      
      if (!idsToUse || idsToUse.length === 0) {
        this.$message({
          type: 'warning',
          message: '请选择要标记的学生'
        });
        return;
      }
      
      this.$http.post('/correctionRecords/batch-create', {
        studentIds: idsToUse,
        date: this.localCurrentDate,
        homeworkTypeId: this.localSelectedType,
        classId: this.classId
      })
      .then(response => {
        const { createdCount, updatedCount, totalCount, autoAssigned, failedStudentNames } = response.data.data;
        
        this.$message({
          type: 'success',
          message: response.data.message
        });
        
        if (autoAssigned) {
          this.$emit('homework-assigned');
        } else {
          this.$emit('loadData');
        }
        
        if (this.showUnfinishedCount) {
          this.$emit('loadUnfinishedCounts');
        }
      })
      .catch(error => {
        this.$message({
          type: 'error',
          message: '标记失败: ' + (error.response?.data?.message || error.message || '未知错误')
        });
      });
      
      this.manualQuickRecordVisible = false;
      this.manualQuickRecordSelected = [];
    },
    
    handleMoveToRight(selectedStudents) {
      this.manualQuickRecordSelected = selectedStudents;
    },
    
    handleMoveToLeft(selectedStudents) {
      this.manualQuickRecordSelected = selectedStudents;
    },
    
    getSubjectIdByHomeworkType(homeworkTypeId) {
      return this.subjectIndex + 1;
    },
    
    handleManualQuickRecordFilterMethod(query, item) {
      if (!query) return true;
      const lowerQuery = query.toLowerCase();
      return item.label.toLowerCase().includes(lowerQuery) ||
             item.pinyinInitials.toLowerCase().includes(lowerQuery);
    },
    
    handleSelectedChanged(selected) {
      this.manualQuickRecordSelected = selected;
    },
    
    // 查询学生
    querySearch(queryString, cb) {
      // 当queryString为空时，显示所有学生（按学号排序）
      if (!queryString) {
        // 创建学生列表的副本并按学号排序
        const sortedStudents = [...this.students].sort((a, b) => {
          return a.studentNumber.localeCompare(b.studentNumber, undefined, {
            numeric: true,
            sensitivity: 'base'
          })
        })
        cb(sortedStudents)
        return
      }

      // 当有查询字符串时，按原有逻辑过滤学生
      let searchUrl = `/students/search?keyword=${queryString}`
      if (this.classId) {
        searchUrl = `/students/search?keyword=${queryString}&classId=${this.classId}`
      }
      
      this.$http.get(searchUrl)
        .then(response => {
          cb(response.data)
        })
    },
    
    // 处理选择学生
    handleSelectStudent(item) {
      // 立即显示弹窗，显示loading状态
      this.selectedStudentName = item.name;
      this.studentHistoryRecords = []; // 先清空数据
      this.studentHistoryDialogVisible = true;
      
      // 获取学生历史欠交记录
      let url = `/correctionRecords/studentUnfinishedAllTypesWithContent?studentId=${item.id}&subjectId=${this.subjectIndex + 1}`;
      
      // 如果有classId，添加到URL中
      if (this.classId) {
        url += `&classId=${this.classId}`;
      }
      
      this.$http.get(url)
        .then(response => {
          // 过滤掉未来日期的记录
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          let studentHistoryRecords = response.data
            .filter(record => {
              const recordDate = new Date(record.date);
              recordDate.setHours(0, 0, 0, 0);
              return recordDate <= today;
            })
            .map(record => {
              return {
                ...record,
                markedAsFinished: !!record.corrected
              };
            });

          // 更新数据，弹窗已经在loading状态显示
          this.studentHistoryRecords = studentHistoryRecords;
              
          // 如果没有记录，显示完成信息
          if (studentHistoryRecords.length === 0) {
            this.showStudentNoRecords = true;
          } else {
            this.showStudentNoRecords = false;
          }
        });
    },
    
    // 显示欠交学生对话框
    showUnfinishedStudents() {
      // 获取未完成学生列表
      let url = `/correctionRecords/unfinishedStudentsWithContent?typeId=${this.localSelectedType}`;
      if (this.classId) {
        url += `&classId=${this.classId}`;
      }
      
      this.$http.get(url)
        .then(response => {
          const unfinishedStudents = response.data.map(item => {
            // 格式化日期
            let formattedDate = item.date;
            if (typeof item.date === 'string' && item.date.includes('T')) {
              const date = new Date(item.date);
              formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            }

            // 计算星期几
            const dateObj = new Date(formattedDate);
            const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
            const weekDay = weekDays[dateObj.getDay()];

            return {
              ...item,
              date: formattedDate,
              weekDay: weekDay,
              homeworkTypeId: this.localSelectedType,
              rawDate: formattedDate,
              formattedDate: formattedDate
            };
          });

          // 直接设置未完成学生列表并显示对话框
          this.unfinishedStudents = unfinishedStudents;
          this.unfinishedStudentsDialogVisible = true;
        });
    },
    
    // 学生历史记录排序
    handleStudentHistorySort({ prop, order }) {
      this.studentHistorySortField = prop;
      this.studentHistorySortOrder = order === 'ascending' ? 'asc' : 'desc';
      
      this.studentHistoryRecords.sort((a, b) => {
        let result = 0;
        
        if (prop === 'date') {
          result = new Date(a.date) - new Date(b.date);
          if (result === 0) {
            result = a.homeworkTypeId - b.homeworkTypeId;
          }
        } else if (prop === 'typeName') {
          result = a.homeworkTypeId - b.homeworkTypeId;
          if (result === 0) {
            result = new Date(a.date) - new Date(b.date);
          }
        }
        
        if (this.studentHistorySortOrder === 'desc') {
          result = -result;
        }
        
        return result;
      });
    },
    
    // 历史欠交学生排序
    handleUnfinishedStudentsSort({ prop, order }) {
      this.unfinishedStudentsSortField = prop;
      this.unfinishedStudentsSortOrder = order === 'ascending' ? 'asc' : 'desc';
      
      this.unfinishedStudents.sort((a, b) => {
        let result = 0;
        
        if (prop === 'studentNumber') {
          const numA = parseInt(a.studentNumber);
          const numB = parseInt(b.studentNumber);
          result = numA - numB;
          if (this.unfinishedStudentsSortOrder === 'desc') {
            result = -result;
          }
          if (result === 0) {
            result = new Date(a.rawDate || a.date) - new Date(b.rawDate || b.date);
          }
        } else if (prop === 'date') {
          result = new Date(a.rawDate || a.date) - new Date(b.rawDate || b.date);
          if (this.unfinishedStudentsSortOrder === 'desc') {
            result = -result;
          }
          if (result === 0) {
            const numA = parseInt(a.studentNumber);
            const numB = parseInt(b.studentNumber);
            result = numA - numB;
          }
        }
        
        return result;
      });
    },
    
    // 彻查作业弹窗的订正方法
    handleStudentHistoryMarkAsFinished(row) {
      if (row.markedAsFinished) {
        row.corrected = false;
        if (this.classId && !row.classId) {
          row.classId = this.classId;
        }
        this.$http.post('/correctionRecords', row)
          .then(() => {
            this.$message({ type: 'success', message: '已取消订正状态!' });
            this.$delete(row, 'markedAsFinished');
            this.$emit('loadData');
            if (this.showUnfinishedCount) {
              this.$emit('loadUnfinishedCounts');
            }
          })
          .catch(error => {
            row.corrected = true;
            this.$message({ type: 'error', message: '取消订正状态失败: ' + (error.response?.data?.message || error.message || '未知错误') });
          });
      } else {
        row.corrected = true;
        if (this.classId && !row.classId) {
          row.classId = this.classId;
        }
        this.$http.post('/correctionRecords', row)
          .then(() => {
            this.$message({ type: 'success', message: '标记成功!' });
            this.$set(row, 'markedAsFinished', true);
            this.$emit('loadData');
            if (this.showUnfinishedCount) {
              this.$emit('loadUnfinishedCounts');
            }
          })
          .catch(error => {
            row.corrected = false;
            this.$message({ type: 'error', message: '标记失败: ' + (error.response?.data?.message || error.message || '未知错误') });
          });
      }
    },
    
    // 历史欠交学生弹窗的订正方法
    handleUnfinishedStudentsMarkAsFinished(row) {
      if (row.markedAsFinished) {
        let url = `/correctionRecords?date=${row.rawDate || row.date}&typeId=${row.homeworkTypeId}`;
        if (this.classId) {
          url = `/correctionRecords?date=${row.rawDate || row.date}&typeId=${row.homeworkTypeId}&classId=${this.classId}`;
        }
        this.$http.get(url)
          .then(response => {
            const student = this.students.find(s => s.studentNumber === row.studentNumber);
            if (student) {
              const record = response.data.find(r => r.studentId === student.id);
              if (record) {
                record.corrected = false;
                if (this.classId && !record.classId) {
                  record.classId = this.classId;
                }
                return this.$http.post('/correctionRecords', record);
              }
            }
            return Promise.reject("未找到对应记录");
          })
          .then(() => {
            this.$delete(row, 'markedAsFinished');
            this.$emit('loadData');
            if (this.showUnfinishedCount) {
              this.$emit('loadUnfinishedCounts');
            }
            this.$message({ type: 'success', message: '已取消订正状态!' });
          })
          .catch(error => {
            this.$message({ type: 'error', message: '取消订正状态失败: ' + (error.response?.data?.message || error.message || '未知错误') });
          });
      } else {
        let url = `/correctionRecords?date=${row.rawDate || row.date}&typeId=${row.homeworkTypeId}`;
        if (this.classId) {
          url = `/correctionRecords?date=${row.rawDate || row.date}&typeId=${row.homeworkTypeId}&classId=${this.classId}`;
        }
        this.$http.get(url)
          .then(response => {
            const student = this.students.find(s => s.studentNumber === row.studentNumber);
            if (student) {
              const record = response.data.find(r => r.studentId === student.id);
              if (record) {
                record.corrected = true;
                if (this.classId && !record.classId) {
                  record.classId = this.classId;
                }
                return this.$http.post('/correctionRecords', record);
              }
            }
            return Promise.reject("未找到对应记录");
          })
          .then(() => {
            this.$set(row, 'markedAsFinished', true);
            this.$emit('loadData');
            if (this.showUnfinishedCount) {
              this.$emit('loadUnfinishedCounts');
            }
            this.$message({ type: 'success', message: '标记成功!' });
          })
          .catch(error => {
            this.$message({ type: 'error', message: '标记失败: ' + (error.response?.data?.message || error.message || '未知错误') });
          });
      }
    },
    
    // 编辑作业内容
    handleEditHomeworkContent(record) {
      this.editingRow = record;
      this.tempHomeworkContent = record.content;
      this.$nextTick(() => {
        this.homeworkContentDialogVisible = true;
      });
    },
    
    // 定位到记录
    handleLocateToRecord(record) {
      let formattedDate = record.date;
      if (typeof record.date === 'string' && record.date.includes('T')) {
        const d = new Date(record.date);
        formattedDate = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
      }
      
      this.localCurrentDate = formattedDate;
      this.$emit('update:currentDate', formattedDate);
      this.$emit('update:selectedType', record.homeworkTypeId);
      this.$emit('loadData');
      
      // 关闭对话框
      this.studentHistoryDialogVisible = false;
      this.showStudentNoRecords = false; // 重置无记录状态
      this.unfinishedStudentsDialogVisible = false;
    },
    
    // 导出学生数据
    handleExportStudentData() {
      Utils.exportStudentData(this);
    },
    
    // 格式化日期字符串
    formatDateString(dateStr) {
      if (!dateStr) return '';
      if (typeof dateStr === 'string' && dateStr.includes('T')) {
        const d = new Date(dateStr);
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
      }
      return dateStr;
    },
    
    // 格式化日期
    formatDate(dateString) {
      const date = dateString instanceof Date ? dateString : new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 日期选择器失焦
    onDatePickerBlur() {
      // 可以添加额外的失焦处理逻辑
    },
    
    // 日期选择器聚焦
    onDatePickerFocus() {
      // 可以添加额外的聚焦处理逻辑
    },
    
    // 触发布置作业按钮闪烁
    triggerAssignHomeworkFlash() {
      this.shouldFlash = true;
      setTimeout(() => {
        this.shouldFlash = false;
      }, 400);
    },
    
    // 处理布置作业按钮点击
    handleAssignHomework() {
      if (!this.classId || !this.localCurrentDate || !this.localSelectedType) {
        this.$message.warning('参数不完整，无法布置作业');
        return;
      }
      
      this.$http.post(`/homeworkAssignments/assign?classId=${this.classId}&date=${this.localCurrentDate}&homeworkTypeId=${this.localSelectedType}`)
        .then(response => {
          if (response.data.success) {
            this.$message.success(response.data.message || '布置作业成功');
            this.$emit('homework-assigned');
          } else {
            this.$message.error(response.data.message || '布置作业失败');
          }
        })
        .catch(error => {
          console.error('布置作业失败:', error);
          this.$message.error('布置作业失败: ' + (error.response?.data?.message || error.message || '未知错误'));
        });
    }
  },
  mounted() {
    // 监听布置作业按钮闪烁事件
    EventBus.$on('trigger-assign-homework-flash', this.triggerAssignHomeworkFlash);
  },
  beforeDestroy() {
    // 移除事件监听
    EventBus.$off('trigger-assign-homework-flash', this.triggerAssignHomeworkFlash);
  }
}
</script>

<style scoped>
/* 控制面板容器样式 */
.controls {
  margin-bottom: 15px;
  padding: 10px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.date-type-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
}

.type-selector-container {
  flex: 1;
  min-width: 0;
}

.stat-buttons-row {
  display: flex;
  gap: 5px;
  margin-top: 8px;
  width: 100%;
}

.stat-button-item {
  flex: 1;
  text-align: center;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 10px 4px;
}

.stat-button-item.assign-homework {
  flex: 2;
}

.assign-homework-btn {
  width: 100%;
  height: 45px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.assign-homework-btn.chinese-subject {
  background: linear-gradient(135deg, #ff9800 0%, #ff6d00 100%);
}

.assign-homework-btn.math-subject {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

.assign-homework-btn.english-subject {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%);
}

.assign-homework-btn.flash-animation {
  animation: flash-animation 0.1s ease-in-out 4;
}

@keyframes flash-animation {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.stat-button-item .stat-number {
  font-size: 20px;
  font-weight: bold;
}

.stat-button-item .stat-number.finished {
  color: #67c23a;
}

.stat-button-item .stat-number.unfinished {
  color: #f56c6c;
}

.stat-button-item .stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 2px;
}

.stat-button-item .el-button {
  width: 100%;
  font-size: 13px;
  padding: 12px 4px;
}

.btn-text-two-lines {
  line-height: 1.4;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.unfinished-count-btn {
  background: #f5f7fa;
  border-color: #dcdfe6;
  color: #606266;
  border-radius: 10px;
}

.unfinished-count-btn.active {
  background: #409eff;
  border-color: #409eff;
  color: white;
}

.date-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.date-picker-container {
  width: 100%;
}

.nav-buttons {
  display: flex;
  gap: 5px;
  margin-bottom: 2px;
}

.nav-buttons .el-button {
  flex: 1;
  font-size: 13px;
  padding: 6px 4px;
  border-radius: 10px;
  font-weight: bold;
}

@media (pointer: coarse) {
  .nav-buttons {
    flex-direction: row;
  }
  
  .nav-buttons .el-button {
    flex: 1;
    padding: 6px 8px;
    border-radius: 10px;
    font-weight: bold;
  }
}

@media (orientation: portrait) {
  .nav-buttons {
    flex-direction: row;
  }
  
  .nav-buttons .el-button {
    flex: 1;
    padding: 6px 4px;
    border-radius: 10px;
    font-weight: bold;
  }
}

.content-input-container {
  width: 100%;
  display: flex;
  gap: 8px;
}

.content-input-container .el-input {
  flex: 1;
}

.subject-toggle-btn-inline {
  width: 80px;
  height: 40px;
  font-size: 14px;
  font-weight: bold;
  padding: 6px;
  text-align: center;
  margin: 0;
  border-radius: 10px;
  box-shadow: 0 3px 6px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  color: white;
  flex-shrink: 0;
}

.subject-toggle-btn-inline.chinese-subject {
  background: linear-gradient(135deg, #FFA500, #FF8C00);
  border: 2px solid #FFA500;
}

.subject-toggle-btn-inline.math-subject {
  background: linear-gradient(135deg, #409EFF, #64b5f6);
  border: 2px solid #409EFF;
}

.subject-toggle-btn-inline.english-subject {
  background: linear-gradient(135deg, #F56C6C, #ff4242);
  border: 2px solid #F56C6C;
}

.subject-toggle-btn-inline:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
}

.controls .control-group .content-input-container .el-input {
  border-radius: 10px !important;
}

.controls .control-group .content-input-container .el-input__inner {
  border-radius: 10px !important;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 8px;
  margin-top: 2px;
  margin-bottom: 10px;
  width: 100%;
}

.action-buttons .el-button {
  flex: 1;
  font-size: 13px;
  padding: 8px 4px;
  border-radius: 10px;
  font-weight: bold;
  min-width: 0;
  text-align: center;
}

.search-container {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 语文主题控制区域 */
body.chinese-theme .controls {
  background-color: #fff8dc;
}

/* 数学主题控制区域 */
body.math-theme .controls {
  background-color: #f0f8ff;
}

/* 英语主题控制区域 */
body.english-theme .controls {
  background-color: #fff0f5;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .date-type-row {
    flex-direction: row;
    flex-wrap: wrap;
  }
  
  .type-selector-container {
    flex: 1;
  }
  
  .date-controls {
    flex: 1;
  }
  
  .nav-buttons {
    flex-direction: row;
    flex-wrap: wrap;
  }
  
  .action-buttons {
    flex-direction: row;
  }
  
  .action-buttons .el-button {
    padding: 8px 4px;
    font-size: 13px;
    flex: 1;
    border-radius: 10px;
    font-weight: bold;
  }
}

/* 小屏幕适配 */
@media (max-width: 480px) {
  .date-type-row {
    flex-direction: row;
    flex-wrap: nowrap;
  }
  
  .action-buttons {
    gap: 5px;
  }
  
  .action-buttons .el-button {
    font-size: 12px;
    padding: 6px 4px;
    border-radius: 10px;
    font-weight: bold;
  }
}

/* 主题色确认对话框样式 */
.chinese-subject-confirm.el-message-box .el-message-box__header {
  background: linear-gradient(135deg, #ff9800 0%, #ff6d00 100%) !important;
}

.chinese-subject-confirm.el-message-box .el-message-box__title {
  color: #fff !important;
}

.chinese-subject-confirm.el-message-box .el-message-box__btns .el-button--primary {
  background: linear-gradient(135deg, #ff9800 0%, #ff6d00 100%) !important;
  border-color: #ff6d00 !important;
}

.math-subject-confirm.el-message-box .el-message-box__header {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%) !important;
}

.math-subject-confirm.el-message-box .el-message-box__title {
  color: #fff !important;
}

.math-subject-confirm.el-message-box .el-message-box__btns .el-button--primary {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%) !important;
  border-color: #1976d2 !important;
}

.english-subject-confirm.el-message-box .el-message-box__header {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%) !important;
}

.english-subject-confirm.el-message-box .el-message-box__title {
  color: #fff !important;
}

.english-subject-confirm.el-message-box .el-message-box__btns .el-button--primary {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%) !important;
  border-color: #c2185b !important;
}
</style>
